import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DatePipe } from '@angular/common';
import * as uuid from 'uuid';
import { EMPTY, Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import {
    IPataviumPagedResponse,
    IPataviumResponseFirstItem,
    PataviumPagedResponseFirstItem
} from '../helpers/response.class';
import { IStorageFile } from '../models/request-and-responses/rest-api/interfaces/storageFile';

export enum ContentTypeSupported {
    Zip = 0,
    Csv = 1,
    OctetStream = 2,
    Pdf = 3,
    Mp3 = 4
}

export enum DownloadType {
    STREAM = 0,
    PDFVIEWER = 1
}

export class PairContentTypeExtension {
    constructor(ctype: string, ext: string) {
        this.Extension = ext;
        this.ContentType = ctype;
    }
    Extension: string;
    ContentType: string;
}

@Injectable()
export class FileDownloadService {
    readonly ContentTypeMap: Map<ContentTypeSupported, PairContentTypeExtension> = new Map([
        [ContentTypeSupported.Csv, new PairContentTypeExtension('text/csv', 'csv')],
        [ContentTypeSupported.OctetStream, new PairContentTypeExtension('octet-stream', '')],
        [ContentTypeSupported.Pdf, new PairContentTypeExtension('application/pdf', 'pdf')],
        [ContentTypeSupported.Zip, new PairContentTypeExtension('application/zip', 'zip')]
    ]);

    constructor(
        private sanitizer: DomSanitizer,
        private datePipe: DatePipe,
        private http: HttpClient
    ) {}

    private static _getUrl(buffer: ArrayBuffer, contentType: string): string {
        const file = new Blob([buffer], { type: contentType });
        return window.URL.createObjectURL(file);
    }

    private _getSecureUrl(buffer: ArrayBuffer, contentType: string) {
        const url = FileDownloadService._getUrl(buffer, contentType);
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    private async _download(
        blobUrl: SafeResourceUrl,
        fileName: string = null,
        ext: string = '',
        wantGenerationDatetime: boolean = false,
        wantUuid: boolean = false
    ): Promise<void> {
        //Remove extenion if you don't want it...
        if (ext !== '') {
            const whereIsDot = fileName.indexOf('.');
            if (whereIsDot >= 0) {
                fileName = fileName.substr(0, whereIsDot);
            }
        }
        //Starting to create temporary <a>.
        const anchor = document.createElement('a');
        //If filename is empty, I'll create one of it with uuid
        if (fileName) {
            anchor.download = fileName;
        } else {
            anchor.download = 'gen' + uuid.v4();
        }
        if (wantGenerationDatetime) {
            const thisDatetime = this.datePipe.transform(Date.now(), 'yyyyMMdd_HHmmss');
            anchor.download += '_' + thisDatetime;
        }
        if (wantUuid) {
            anchor.download += uuid.v4();
        }
        if (ext) {
            anchor.download += '.' + ext;
        }
        anchor.href = this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, blobUrl);
        anchor.click();
        anchor.remove();
    }

    private _displayFile(secureUrl: SafeResourceUrl): void {
        window.open(this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, secureUrl));
    }

    async generateAndDownload(
        buffer: ArrayBuffer,
        contentType: ContentTypeSupported = ContentTypeSupported.OctetStream,
        filename: string = null,
        wantGenerationDatetime: boolean = false,
        wantUuid: boolean = false
    ): Promise<void> {
        const extType = this.ContentTypeMap.get(contentType).Extension;
        const cType = this.ContentTypeMap.get(contentType).ContentType;
        const blob = this._getSecureUrl(buffer, cType);
        await this._download(blob, filename, extType, wantGenerationDatetime, wantUuid);
    }

    downloadDocument(id: number): Observable<ArrayBuffer> {
        return this.http.get(environment.apiUrl + '/files/stream/' + id, {
            responseType: 'arraybuffer'
        });
    }

    async downloadAndOpenDocument(fileId, filename, downloadType: DownloadType = DownloadType.STREAM) {
        let cType: string = null;
        this.downloadDocument(fileId)
            .pipe(
                tap(async result => {
                    switch (downloadType) {
                        case DownloadType.STREAM:
                            cType = this.ContentTypeMap.get(ContentTypeSupported.OctetStream)
                                .ContentType;
                            await this._download(this._getSecureUrl(result, cType), filename, '');
                            break;
                        case DownloadType.PDFVIEWER:
                            cType = this.ContentTypeMap.get(ContentTypeSupported.Pdf).ContentType;
                            await this._displayFile(this._getSecureUrl(result, cType));
                            break;
                        default:
                            return;
                    }
                }),
                catchError(err => {
                    console.error(`Caught error`, err);
                    return EMPTY;
                }))
            .subscribe();
    }

    getFileInfos(id: number): Observable<IPataviumResponseFirstItem<IStorageFile>> {
        return this.http
            .get<IPataviumPagedResponse<IStorageFile>>(`${environment.apiUrl}/files/${id}`)
            .pipe(map(response => new PataviumPagedResponseFirstItem(response)));
    }

    getSecureUrlForFile(id): Observable<SafeResourceUrl> {
        return this.getFileInfos(id)
            .pipe(
                switchMap(fileInfos =>
                    this.downloadDocument(id)
                        .pipe(
                            map(arrBuff => this._getSecureUrl(arrBuff, fileInfos.item.contentType))
                        )
                )
            );
    }

    download(buffer: ArrayBuffer, mimeType: string, name: string): void {
        const url = FileDownloadService._getUrl(buffer, mimeType);
        const link = document.createElement('a');
        link.download = name;
        link.href = url;
        link.click();
        link.remove();
    }


    downloadTextPlain(content: string, fileName: string): void {
        const u8Arr: Uint8Array = new TextEncoder().encode(content);
        const buffer: ArrayBuffer = u8Arr.buffer as ArrayBuffer;
        this.download(buffer, 'text/plain', fileName);
    }
}



// WEBPACK FOOTER //
// ./src/app/services/file-download.service.ts